// @flow
import React from 'react';
import { Shaders, Node, GLSL } from 'gl-react';

export type ShaderSettings = {
  brightness: number,
  contrast: number,
  thickness: number,
  blend: string,
  color: string,
};

type Props = {
  settings: ShaderSettings,
  children: any,
};

const shaders = Shaders.create({
  lineShader: {
    frag: GLSL`
    precision highp float;

    #define M_PI 3.1415926535897932384626433832795

    uniform int angle;
    uniform int effectColor;
    uniform float brightness;
    uniform float contrast;
    uniform sampler2D tex;
    varying vec2 uv;
    uniform int style;
    uniform int blend;
    uniform float thickness;


    float balance(float sample, float weight) {
      // if weight < 1.0 use value 1 else value 2 from mix
      weight = mix(sample + weight, weight, step(1.0, weight));

      return clamp(pow(weight, 5.0), 0.0, 1.0);
    }

    mat2 rotateAngle(float angle) {
      float radian = float(angle) * (180.0 / M_PI);
      return mat2(cos(radian), -sin(radian), sin(radian), cos(radian));
    }

    float diagonal(float sample) {
      vec2 pixel = floor(vec2(gl_FragCoord)) * rotateAngle(float(angle));
      float a = 1.0;
      float b = mod(pixel.x - pixel.y, thickness);
      float c = thickness / 2.0;

      // if b < thickness use value 1 else value 2 from mix
      a = mix(abs(b - c) / c, a, step(thickness, b));

      return balance(sample, a);
    }

    vec3 blendMultiply(vec3 base, vec3 blend) {
      return base*blend;
    }

    float blendDarken(float base, float blend) {
      return min(blend,base);
    }

    vec3 blendDarken(vec3 base, vec3 blend) {
      return vec3(blendDarken(base.r,blend.r),blendDarken(base.g,blend.g),blendDarken(base.b,blend.b));
    }

    float blendScreen(float base, float blend) {
      return 1.0-((1.0-base)*(1.0-blend));
    }

    vec3 blendScreen(vec3 base, vec3 blend) {
      return vec3(blendScreen(base.r,blend.r),blendScreen(base.g,blend.g),blendScreen(base.b,blend.b));
    }

    void main(void) {
      vec3 color = texture2D(tex, uv).rgb;
      vec3 colorContrasted = ((color - 0.5) * max(contrast, 0.0)) + 0.5;
      color = colorContrasted + vec3(brightness);

      float hatch;
      float scale = min(min(color.r, color.g), color.b);
      if (thickness <= 0.0) {
        gl_FragColor = vec4(color, 1);
        return;
      }
      else if (style == 0)
        hatch = diagonal(scale);
      else
        hatch = 1.0;

      // if 0.25 < hatch use value 1 else value 2 from mix
      color = mix(vec3(1.0), color, step(hatch, 0.25));

      vec3 hatchedColor = color * hatch;
      vec3 blendedColor = hatchedColor;

      float rIntValue = mod(float(effectColor / 256 / 256), 256.0);
      float gIntValue = mod(float(effectColor / 256)      , 256.0);
      float bIntValue = mod(float(effectColor)            , 256.0);

      vec3 fColor = vec3(rIntValue / 255.0, gIntValue / 255.0, bIntValue / 255.0);

      if (blend == 1)
        blendedColor = blendMultiply(blendedColor, fColor);
      else if (blend == 2)
        blendedColor = blendScreen(blendedColor, fColor);

      gl_FragColor = vec4(blendedColor, 1);
    }`,
  },
});

class LineShader extends React.Component<Props> {
  static defaultProps = {
    settings: {
      brightness: 0,
      contrast: 1,
      thickness: 10,
      blend: 'none',
      color: '#fff',
    },
  };

  hexToRgb = hex => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
          parseInt(result[1], 16) / 256.0,
          parseInt(result[2], 16) / 256.0,
          parseInt(result[3], 16) / 256.0,
        ]
      : null;
  };

  render() {
    const scale = window.devicePixelRatio;
    const { settings, children: tex } = this.props;

    const { thickness, color, blend: blendString } = settings;
    let { contrast, brightness } = settings;

    const hexColor = parseInt(color.replace(/^#/, ''), 16);

    let blend = 0;
    if (blendString === 'multiply') {
      blend = 1;
    } else if (blendString === 'screen') {
      blend = 2;
    }

    contrast = (contrast / 100) * 2;
    brightness = -1 + (brightness / 100) * 2;

    return (
      <Node
        shader={shaders.lineShader}
        uniforms={{
          style: 0,
          thickness: thickness * scale,
          tex,
          brightness,
          contrast,
          blend,
          effectColor: hexColor,
          angle: 65,
        }}
      />
    );
  }
}

export default LineShader;



// WEBPACK FOOTER //
// /opt/build/repo/src/app/components/shaders/LineShader/LineShader.js